import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "services/api";
import emailValidate from "services/emailValidate";
import { getAffiliateToken } from "utils/affiliateAttribution";
import Translator, { __ } from "services/Translator";
import SignupBonusAccordion from "components/SignupBonusAccordion";
import AuthInputWrap from "components/AuthInputWrap";
import { sanitizeTextInput } from "utils/profileValidation";

export default function SignupFormPanel({
  referralCode,
  onSuccess,
  setSpinner,
  closeLoginModal,
  prefilledEmail,
  prefilledBonusOffer,
  hideEmailField = false,
  hideBonusSelector = false,
  showBackButton = false,
  onBack,
  onBonusOfferChange,
  variant = "default",
}) {
  const selectedLanguage = Translator.getSelectedLanguage();
  const [showPass, setShowPass] = useState(false);
  const [regErrors, setRegErrors] = useState({});
  const [isRegistering, setIsRegistering] = useState(false);
  const [activeBonusAccordionKey, setActiveBonusAccordionKey] = useState(null);
  const [hasManualBonusSelection, setHasManualBonusSelection] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [isCampaignsLoading, setIsCampaignsLoading] = useState(true);

  const [registrationForm, setRegistrationForm] = useState({
    username: "",
    email: prefilledEmail || "",
    password: "",
    affiliateCode: "",
    referralCode: referralCode || "",
    language: selectedLanguage?.code,
    bonusOffer: prefilledBonusOffer || "no_bonus",
    agreeToTerms: false,
    ageConfirmation: false,
  });

  const updateRegistrationForm = (field, value) => {
    setRegistrationForm((prev) => ({ ...prev, [field]: value }));
    setRegErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const getCampaigns = async () => {
    setIsCampaignsLoading(true);
    try {
      const response = await api.post("/get-registration-campaigns", {
        language: selectedLanguage?.code,
        currency: "USD",
      });
      if (response.status === 200) {
        setCampaigns(Array.isArray(response.data) ? response.data : []);
      }
    } finally {
      setIsCampaignsLoading(false);
    }
  };

  useEffect(() => {
    getCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage?.code]);

  useEffect(() => {
    if (hideBonusSelector && prefilledBonusOffer) {
      updateRegistrationForm("bonusOffer", prefilledBonusOffer);
      return;
    }
    if (hasManualBonusSelection) return;
    if (!campaigns.length || isCampaignsLoading) return;
    updateRegistrationForm("bonusOffer", campaigns[0].id);
  }, [campaigns, hasManualBonusSelection, isCampaignsLoading, hideBonusSelector, prefilledBonusOffer]);

  useEffect(() => {
    if (prefilledEmail) {
      updateRegistrationForm("email", prefilledEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledEmail]);

  useEffect(() => {
    onBonusOfferChange?.(registrationForm.bonusOffer);
  }, [registrationForm.bonusOffer, onBonusOfferChange]);

  useEffect(() => {
    if (referralCode) {
      updateRegistrationForm("referralCode", referralCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralCode]);

  const handleBonusSelection = (value) => {
    updateRegistrationForm("bonusOffer", value);
    setHasManualBonusSelection(true);
    setActiveBonusAccordionKey(null);
  };

  const updateRegInput = (ev) => {
    const target = ev.target;
    const field = target.name;
    let value = target.type === "checkbox" ? target.checked : target.value;

    if (field === "username" || field === "email") {
      value = sanitizeTextInput(value, 250);
    }
    if (field === "password") {
      value = String(value).slice(0, 30);
    }

    updateRegistrationForm(field, value);
  };

  const validateRegisterForm = async () => {
    const errorsTmp = {};
    let valid = true;

    const username = sanitizeTextInput(registrationForm.username, 250);
    if (username.length < 2 || username.length > 250) {
      errorsTmp.username = __("Please enter the user name");
      valid = false;
    } else if (emailValidate(username) || username.includes("@")) {
      errorsTmp.username = __("Email address cannot be used as username");
      valid = false;
    } else {
      const usernameCheck = await api.post("/check-reg-duplicate-username", { username });
      if (usernameCheck.status !== 200 && usernameCheck.data?.errors?.username) {
        errorsTmp.username = usernameCheck.data.errors.username;
        valid = false;
      }
    }

    const email = sanitizeTextInput(registrationForm.email, 250).toLowerCase();
    if (!emailValidate(email)) {
      errorsTmp.email = __("Please enter a valid email address");
      valid = false;
    } else {
      const emailCheck = await api.post("/check-reg-duplicate-email", { email });
      if (emailCheck.status !== 200) {
        errorsTmp.email =
          emailCheck.data?.errors?.email || __("This email address is already in use.");
        valid = false;
      }
    }

    if (registrationForm.password.length < 8 || registrationForm.password.length > 30) {
      errorsTmp.password = __("Your password must contain between 8 and 30 characters");
      valid = false;
    }

    if (!registrationForm.bonusOffer) {
      errorsTmp.bonusOffer = __("Please select a bonus offer");
      valid = false;
    }

    if (!registrationForm.agreeToTerms) {
      errorsTmp.agreeToTerms = __("Please check the terms and conditions.");
      valid = false;
    }

    if (!registrationForm.ageConfirmation) {
      errorsTmp.ageConfirmation = __("Please check the age confirmation.");
      valid = false;
    }

    setRegErrors(errorsTmp);
    return valid;
  };

  const registrationAction = async (ev) => {
    ev.preventDefault();
    if (isRegistering) return;

    const valid = await validateRegisterForm();
    if (!valid) return;

    setIsRegistering(true);
    setSpinner?.(true);

    const payload = {
      username: sanitizeTextInput(registrationForm.username, 250),
      email: sanitizeTextInput(registrationForm.email, 250).toLowerCase(),
      password: registrationForm.password,
      affiliateCode: sanitizeTextInput(registrationForm.affiliateCode, 80),
      referralCode: sanitizeTextInput(registrationForm.referralCode, 80),
      bonusOffer: registrationForm.bonusOffer,
      agreeToTerms: registrationForm.agreeToTerms ? 1 : 0,
      ageConfirmation: registrationForm.ageConfirmation ? 1 : 0,
      language: registrationForm.language,
    };

    const affToken = getAffiliateToken();
    if (affToken) payload.affiliateToken = affToken;

    try {
      const response = await api.post("/quick-signup", payload);
      if (response.status === 200) {
        onSuccess?.(payload.email, payload.password);
      } else if (response.data?.errors) {
        setRegErrors(response.data.errors);
      } else {
        alert(response.data?.message || __("Registration failed"));
      }
    } catch (err) {
      alert(err?.message || __("Registration failed"));
    } finally {
      setSpinner?.(false);
      setIsRegistering(false);
    }
  };

  return (
    <form autoComplete="off" onSubmit={registrationAction}>
      <div
        className={
          "form_area signup-form-panel" + (variant === "betmode" ? " signup-form-panel--betmode" : "")
        }
      >
        {showBackButton ? (
          <button
            type="button"
            className={variant === "betmode" ? "betmode-auth__back-link" : "streamlined-auth__back"}
            onClick={onBack}
          >
            {__("Use a different email")}
          </button>
        ) : null}

        {!hideBonusSelector ? (
          <>
            <SignupBonusAccordion
              campaigns={campaigns}
              isCampaignsLoading={isCampaignsLoading}
              bonusOffer={registrationForm.bonusOffer}
              onSelectBonus={handleBonusSelection}
              activeKey={activeBonusAccordionKey}
              onAccordionSelect={setActiveBonusAccordionKey}
            />
            {regErrors.bonusOffer ? <div className="errorMsg">{regErrors.bonusOffer}</div> : null}
          </>
        ) : null}

        <div className="input_item">
          <div className="name">
            {__("Username")}
            <span className="requried_icon">*</span>
          </div>
          <AuthInputWrap icon="far fa-user">
            <input
              type="text"
              name="username"
              placeholder={__("Username")}
              onChange={updateRegInput}
              value={registrationForm.username}
              className={"name_value " + (regErrors.username ? "error" : "")}
              autoComplete="off"
            />
          </AuthInputWrap>
          <div className="errorMsg">{regErrors.username}</div>
        </div>

        {!hideEmailField ? (
          <div className="input_item">
            <div className="name">
              {__("Email")}
              <span className="requried_icon">*</span>
            </div>
            <AuthInputWrap icon="far fa-envelope">
              <input
                type="email"
                name="email"
                placeholder={__("Your Email")}
                onChange={updateRegInput}
                value={registrationForm.email}
                className={"name_value " + (regErrors.email ? "error" : "")}
                autoComplete="off"
              />
            </AuthInputWrap>
            <div className="errorMsg">{regErrors.email}</div>
          </div>
        ) : null}

        <div className="input_item">
          <div className="name">
            {__("Password")}
            <span className="requried_icon">*</span>
          </div>
          <AuthInputWrap icon="far fa-lock" password>
            <input
              type={showPass ? "text" : "password"}
              name="password"
              placeholder={__("Enter Password")}
              onChange={updateRegInput}
              value={registrationForm.password}
              className={"name_value " + (regErrors.password ? "error" : "")}
              autoComplete="off"
            />
            {showPass ? (
              <i
                className="show_hide_pass fa fa-eye-slash"
                onClick={() => setShowPass(false)}
                aria-hidden="true"
              ></i>
            ) : (
              <i
                className="show_hide_pass fa fa-eye"
                onClick={() => setShowPass(true)}
                aria-hidden="true"
              ></i>
            )}
          </AuthInputWrap>
          <div className="errorMsg">{regErrors.password}</div>
        </div>

        <div className="input_item">
          <div className="reg_check_area">
            <input
              type="checkbox"
              className="stepTerms"
              id="ageConfirmation"
              checked={Boolean(registrationForm.ageConfirmation)}
              name="ageConfirmation"
              onChange={updateRegInput}
            />
            <label htmlFor="ageConfirmation" className="check_area">
              <div className="check_check_and_text_area">
                <div className="check_box">
                  <div className="check_checked_box"></div>
                </div>
                <div className="check_text">
                  {__(
                    "I declare that I am 18 years or older, located in a permitted territory and have no self-exclusions."
                  )}
                  <span className="requried_icon">*</span>
                </div>
              </div>
            </label>
            <div className="errorMsg">{regErrors.ageConfirmation}</div>
          </div>

          <div className="reg_check_area">
            <input
              type="checkbox"
              className="stepTerms"
              id="agreeToTerms"
              checked={Boolean(registrationForm.agreeToTerms)}
              name="agreeToTerms"
              onChange={updateRegInput}
            />
            <label htmlFor="agreeToTerms" className="check_area">
              <div className="check_check_and_text_area">
                <div className="check_box">
                  <div className="check_checked_box"></div>
                </div>
                <div className="check_text">
                  {__("I have read and accept the") + " "}
                  <Link
                    hreflang={Translator.getSelectedLanguage().code}
                    rel="nofollow noopener noreferrer"
                    to="/terms-and-conditions"
                    onClick={closeLoginModal}
                  >
                    {__("Terms of service")}
                  </Link>
                  ,{" "}
                  <Link
                    hreflang={Translator.getSelectedLanguage().code}
                    rel="nofollow noopener noreferrer"
                    to="/privacy-policy"
                    onClick={closeLoginModal}
                  >
                    {__("Privacy policy")}
                  </Link>
                  ,{" "}
                  <Link
                    hreflang={Translator.getSelectedLanguage().code}
                    rel="nofollow noopener noreferrer"
                    to="/responsible-gaming"
                    onClick={closeLoginModal}
                  >
                    {__("Responsible gaming policy")}
                  </Link>
                  ,{" "}
                  <Link
                    hreflang={Translator.getSelectedLanguage().code}
                    rel="nofollow noopener noreferrer"
                    to="/aml-policy"
                    onClick={closeLoginModal}
                  >
                    {__("Payment policy")}
                  </Link>{" "}
                  {__("and any other policies.")}
                  <span className="requried_icon">*</span>
                </div>
              </div>
            </label>
            <div className="errorMsg">{regErrors.agreeToTerms}</div>
          </div>
        </div>

        <div className="login_button">
          <button
            type="submit"
            className={variant === "betmode" ? "betmode-auth__submit" : "commonBtn pTwoBtn"}
            disabled={isRegistering}
          >
            {__("Play Now")}
          </button>
        </div>
      </div>
    </form>
  );
}
